// frontend/src/components/game/managers/MapManager/ActionIconsLayer.jsx
import React from 'react';
import { shallow } from 'zustand/shallow';
import useMapStore from '@store/useMapStore';
import useGameStore from '@store/useGameStore';
import PlannedActionIcon from '@components/ui/game/planned-action/PlannedActionIcon';
import ProcessingIndicator from '@components/ui/game/planned-action/ProcessingIndicator';
import StartingPositionIcon from '@components/ui/game/starting-position-icon/StartingPositionIcon';
import { EMPTY_ARRAY } from '@utils/constants';
import { cancelAction } from '@services/actionService';
import './ActionIconsLayer.css';

const ActionIconsLayer = ({ tileSize }) => {
  const interactionMode = useMapStore((state) => state.interactionMode);
  const processingTiles = useMapStore((state) => state.visual?.processingTiles, shallow) || EMPTY_ARRAY;

  // Use new getters from gameStore
  const { game, isResolutionPhase, getCurrentUnits, getHydratedPlannedActions } = useGameStore.getState();
  const gameId = game?.id;

  const isDeployOrLobby = !game || game.status === 'game_deploy' || game.status === 'game_lobby';
  const hasProcessingTiles = processingTiles.length > 0;

  // During deploy/lobby, only render if there are processing tiles
  if (isDeployOrLobby && !hasProcessingTiles) {
    return null;
  }
  
  // Group planned actions by target tile (moved from useMapStore)
  const getActionIconGroups = () => {
    // Skip during deploy/lobby - no planned actions exist
    if (isDeployOrLobby) return EMPTY_ARRAY;

    const plannedActions = getHydratedPlannedActions();
    //console.log('[ActionIconsLayer] Hydrated planned actions:', plannedActions);
    if (plannedActions.length === 0) return EMPTY_ARRAY;
    
    // Simply use target_data from backend to group actions by tile
    const groupedByTile = plannedActions.reduce((acc, action) => {
      // Skip actions without target data
      if (!action.target_data) {
        console.warn('[ActionIconsLayer] Action missing target data:', action.id);
        return acc;
      }
      
      const key = `${action.target_data.x},${action.target_data.y}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(action);
      return acc;
    }, {});

    // Transform the grouped object into the array format needed for rendering
    return Object.entries(groupedByTile).map(([coordKey, actions]) => {
      const [x, y] = coordKey.split(',').map(Number);
      return {
        position: { x, y },
        actions: actions, // The array of all actions for this tile
      };
    });
  };
  
  const actionIconGroups = getActionIconGroups();
  
  // Show starting positions only for units with first actions during cinematic mode
  const getStartingPositions = () => {
    if (!isResolutionPhase) return [];
    
    const units = getCurrentUnits();
    const plannedActions = getHydratedPlannedActions();
    
    if (units.length === 0 || plannedActions.length === 0) {
      return [];
    }
    
    const startingPositions = [];
    const unitsWithFirstActions = new Set();
    
    // Find units that have first actions (sequence_number === 1 or resolution_order === 1)
    plannedActions.forEach(action => {
      const sequenceNum = action.sequence_number || action.resolution_order;
      if (action.unit_id && sequenceNum === 1 && !unitsWithFirstActions.has(action.unit_id)) {
        unitsWithFirstActions.add(action.unit_id);
        
        // Get the unit using the getter
        const unit = units.find(u => u.id === action.unit_id);
        if (unit && action.origin_data) {
          // Use origin_data for the starting position (where the unit was BEFORE the action)
          startingPositions.push({
            x: action.origin_data.x,
            y: action.origin_data.y,
            unit: unit,
            teamClass: unit.teamClass
          });
        }
      }
    });
    
    return startingPositions;
  };

  const startingPositions = isDeployOrLobby ? [] : getStartingPositions();

  // Nothing to render
  if (actionIconGroups.length === 0 && startingPositions.length === 0 && !hasProcessingTiles) {
    return null;
  }

  // Handler for action deletion
  const handleDeleteAction = (actionId) => {
    if (!gameId) {
      console.error("[ActionIconsLayer] Cannot cancel action: gameId not found.");
      return;
    }
    cancelAction(gameId, actionId);
  };

  return (
    <div className="map-layer action-icons-layer">
      {/* Render planned action icons */}
      {actionIconGroups.map(({ position, actions }) => {
        const key = `action-group-${position.x}-${position.y}`;

        const iconStyle = {
          position: 'absolute',
          left: `${position.x * tileSize}px`,
          top: `${position.y * tileSize}px`,
          width: `${tileSize}px`,
          height: `${tileSize}px`,
        };

        return (
          <div key={key} className="icon-container" style={iconStyle}>
            <PlannedActionIcon 
              actions={actions} 
              interactionMode={interactionMode}
              onDeleteAction={handleDeleteAction}
            />
          </div>
        );
      })}
      
      {/* Render starting position icons */}
      {startingPositions.map(({ x, y, unit, teamClass }) => {
        const key = `starting-pos-${unit.id}`;

        const iconStyle = {
          position: 'absolute',
          left: `${x * tileSize}px`,
          top: `${y * tileSize}px`,
          width: `${tileSize}px`,
          height: `${tileSize}px`,
        };

        return (
          <div key={key} className="icon-container" style={iconStyle}>
            <StartingPositionIcon
              x={x}
              y={y}
              teamClass={teamClass}
            />
          </div>
        );
      })}

      {/* Render processing indicators for pending API calls */}
      {processingTiles.map(({ x, y, type }) => {
        const key = `processing-${x}-${y}`;

        const iconStyle = {
          position: 'absolute',
          left: `${x * tileSize}px`,
          top: `${y * tileSize}px`,
          width: `${tileSize}px`,
          height: `${tileSize}px`,
        };

        return (
          <div key={key} className="icon-container" style={iconStyle}>
            <ProcessingIndicator type={type} />
          </div>
        );
      })}
    </div>
  );
};

export default ActionIconsLayer;